import { useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { Button, Input, Loader, Select, Close } from 'chaoskit/src/components'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import {
  connectCurrentRefinements,
  connectHitsPerPage,
  connectInfiniteHits,
  connectSearchBox,
  connectSortBy,
  connectStateResults,
} from 'react-instantsearch-dom'
import { form, text } from 'chaoskit/src/assets/styles/utility'

import Icon from '~components/Icon'
import mascot from '~media/global/mascot.svg'
import useDebounce from '~hooks/useDebounce'
import Image from '~components/Image'

export const StylesAlgoliaVariables = {
  notificationSize: 15,
}

// Used in updating URL
export const DEBOUNCE_TIME = 500

export const NoResults = () => {
  const theme = useTheme()

  return (
    <div
      css={{
        textAlign: 'center',
      }}
    >
      <Image
        css={{
          height: theme.height.large,
        }}
        src={mascot}
        alt="Gremlin"
      />
      <p
        css={{
          fontSize: theme.fontSize.small,
          color: theme.fontColor.muted,
        }}
      >
        No results found.
      </p>
    </div>
  )
}

// Can't use this due to https://github.com/algolia/react-instantsearch/issues/137
const Content = ({ searching, error, searchResults, children }) => {
  const theme = useTheme()

  if (error) {
    return (
      <div
        css={{
          textAlign: 'center',
        }}
      >
        <Image
          css={{
            height: theme.height.large,
          }}
          src={mascot}
          alt="Gremlin"
        />
        <p
          css={{
            fontSize: theme.fontSize.small,
            color: theme.fontColor.muted,
          }}
        >
          😭 There was an error fetching search results. Please try again later.
        </p>
      </div>
    )
  }

  if (searchResults && searchResults.nbHits === 0) {
    return <NoResults />
  }

  return (
    <Fragment>
      {searching && (
        <Loader
          css={{
            color: theme.color.primary.base,
            fontSize: theme.fontSize.xlarge,
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />
      )}
      {children}
    </Fragment>
  )
}

Content.propTypes = {
  searching: PropTypes.bool,
  error: PropTypes.bool,
  searchResults: PropTypes.object,
  children: PropTypes.node.isRequired,
}

export const CustomContent = connectStateResults(Content)

const SearchBox = ({ currentRefinement, refine, placeholderText }) => {
  const theme = useTheme()

  const [searchValue, setSearchValue] = useState(currentRefinement)

  // Debounce input changes
  const debouncedSearchValue = useDebounce(searchValue, 150)

  useUpdateEffect(() => {
    refine(debouncedSearchValue)
  }, [debouncedSearchValue])

  return (
    <div
      css={{
        position: 'relative',
      }}
    >
      <Input
        type="search"
        name="query"
        value={searchValue}
        onChange={({ target: { value } }) => setSearchValue(value)}
        prefixIcon="search"
        css={[
          searchValue.length !== 0 && {
            // Add padding to input so we can display reset button
            paddingRight:
              form.variables(theme).padding +
              theme.fontSize.base +
              theme.space.small,
          },
        ]}
        placeholder={placeholderText}
      />
      {searchValue.length !== 0 && (
        <Close
          onClick={() => {
            setSearchValue('')
            refine('')
          }}
          title="Reset query"
          css={{
            position: 'absolute',
            right: form.variables(theme).padding,
            zIndex: 2,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
      )}
    </div>
  )
}

SearchBox.propTypes = {
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
  placeholderText: PropTypes.string.isRequired,
}

export const CustomSearchBox = connectSearchBox(SearchBox)

const ClearRefinements = ({ items, refine }) => {
  const theme = useTheme()

  if (items.length === 0) return null

  return (
    <Fragment>
      <hr />
      <Button
        type="reset"
        css={[
          text.heading(theme),
          {
            color: theme.color.danger.base,

            '.u-contrast &': {
              color: theme.contrast.base,
            },
          },
        ]}
        onClick={() => refine(items)}
      >
        <Icon
          icon="close"
          size="small"
          css={{ marginRight: theme.space.xsmall }}
        />
        Clear filters
      </Button>
    </Fragment>
  )
}

ClearRefinements.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
}

export const CustomClearRefinements = connectCurrentRefinements(
  ClearRefinements
)

const HitsPerPage = ({ items, refine, createURL, currentRefinement }) => {
  const options = []

  items.map((item) => options.push({ value: item.value, label: item.label }))

  return (
    <Select
      name="hitsPerPage"
      defaultValue={currentRefinement}
      options={options}
      onChange={({ target: { value } }) => {
        refine(value)
        createURL(value)
      }}
    />
  )
}

HitsPerPage.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
  currentRefinement: PropTypes.number,
}

export const CustomHitsPerPage = connectHitsPerPage(HitsPerPage)

const SortBy = ({ items, refine, createURL, currentRefinement }) => {
  const options = []

  items.map((item) => options.push({ value: item.value, label: item.label }))

  return (
    <Select
      name="sortBy"
      defaultValue={currentRefinement}
      options={options}
      onChange={({ target: { value } }) => {
        refine(value)
        createURL(value)
      }}
    />
  )
}

SortBy.propTypes = {
  items: PropTypes.array,
  refine: PropTypes.func,
  createURL: PropTypes.func,
  currentRefinement: PropTypes.string,
}

export const CustomSortBy = connectSortBy(SortBy)

const InfiniteHits = ({
  hits,
  hasPrevious,
  refinePrevious,
  hasMore,
  refineNext,
  hitTemplate: HitComponent,
  customCss = {},
}) => {
  const theme = useTheme()

  return (
    <div css={{ position: 'relative', marginTop: theme.space.large }}>
      {hits.length > 0 ? (
        <Fragment>
          {hasPrevious && (
            <div
              css={{
                marginBottom: theme.space.large,
                textAlign: 'center',
              }}
            >
              <Button size="small" type="secondary" onClick={refinePrevious}>
                Show Previous
              </Button>
            </div>
          )}
          {hits.length > 0 && (
            <div
              css={{
                display: 'grid',
                gap: theme.space.base,

                ...customCss,
              }}
            >
              {hits.map((hit) => (
                <HitComponent key={hit.slug} hit={hit} />
              ))}
            </div>
          )}
          {hasMore && (
            <div
              css={{
                marginTop: theme.space.large,
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                gap: theme.space.base,
                alignContent: 'center',

                '&::before, &::after': {
                  content: "''",
                  background: theme.color.border.base,
                  height: 2,
                  display: 'block',
                  width: '100%',
                  alignSelf: 'center',
                },

                [theme.mq.medium]: {
                  marginTop: theme.space.xlarge,
                },
              }}
            >
              <Button type="primary" onClick={refineNext}>
                Discover more
              </Button>
            </div>
          )}
        </Fragment>
      ) : (
        <NoResults />
      )}
    </div>
  )
}

InfiniteHits.propTypes = {
  hits: PropTypes.array,
  hasPrevious: PropTypes.bool,
  hasMore: PropTypes.bool,
  refinePrevious: PropTypes.func,
  refineNext: PropTypes.func,
  hitTemplate: PropTypes.func.isRequired,
  customCss: PropTypes.object,
}

export const CustomInfiniteHits = connectInfiniteHits(InfiniteHits)
